import React, { useState, useEffect } from 'react';
import { 
  Search, Calendar, Clock, User, Phone, Mail, MapPin, Video,
  CheckCircle, XCircle, RefreshCw, Eye, FileText, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';
import { formatDate } from '../../lib/utils';

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  confirmed: { label: 'Confirmé', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  rescheduled: { label: 'Reprogrammé', color: 'bg-blue-500/20 text-blue-400', icon: RefreshCw },
  cancelled: { label: 'Annulé', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  completed: { label: 'Terminé', color: 'bg-gray-500/20 text-gray-400', icon: CheckCircle }
};

export const AppointmentsPage = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [agentNotes, setAgentNotes] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    fetchAppointments();
    fetchSlots();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments');
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlots = async () => {
    try {
      const response = await api.get('/appointments/slots');
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const confirmAppointment = async () => {
    if (!selectedAppointment) return;
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, {
        status: 'confirmed',
        confirmed_date: selectedAppointment.preferred_date,
        confirmed_time: selectedAppointment.preferred_time,
        agent_notes: agentNotes || null
      });
      toast.success('Rendez-vous confirmé');
      fetchAppointments();
      setShowDetail(false);
    } catch (error) {
      console.error('Error confirming:', error);
      toast.error('Erreur de confirmation');
    }
  };

  const rescheduleAppointment = async () => {
    if (!selectedAppointment || !newDate || !newTime) {
      toast.error('Veuillez sélectionner une date et un créneau');
      return;
    }
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, {
        status: 'rescheduled',
        confirmed_date: newDate,
        confirmed_time: newTime,
        agent_notes: agentNotes || null
      });
      toast.success('Rendez-vous reprogrammé');
      fetchAppointments();
      setShowReschedule(false);
      setShowDetail(false);
      setNewDate('');
      setNewTime('');
    } catch (error) {
      console.error('Error rescheduling:', error);
      toast.error('Erreur');
    }
  };

  const cancelAppointment = async () => {
    if (!selectedAppointment) return;
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, {
        status: 'cancelled',
        agent_notes: agentNotes || 'Annulé par l\'agent'
      });
      toast.success('Rendez-vous annulé');
      fetchAppointments();
      setShowDetail(false);
    } catch (error) {
      console.error('Error cancelling:', error);
      toast.error('Erreur');
    }
  };

  const markCompleted = async () => {
    if (!selectedAppointment) return;
    try {
      await api.put(`/appointments/${selectedAppointment.id}`, {
        status: 'completed',
        agent_notes: agentNotes || null
      });
      toast.success('Rendez-vous marqué comme terminé');
      fetchAppointments();
      setShowDetail(false);
    } catch (error) {
      console.error('Error completing:', error);
      toast.error('Erreur');
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    const matchesSearch = 
      apt.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.visa_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      apt.client_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || apt.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Get min date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Gestion des Rendez-vous</h2>
          <p className="text-gray-400">{appointments.length} rendez-vous au total</p>
        </div>
        <Button onClick={fetchAppointments} variant="outline" className="border-white/10 text-gray-300 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-400" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {appointments.filter(a => a.status === 'pending').length}
                </p>
                <p className="text-xs text-gray-400">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {appointments.filter(a => a.status === 'confirmed').length}
                </p>
                <p className="text-xs text-gray-400">Confirmés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-500/10 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {appointments.filter(a => a.status === 'rescheduled').length}
                </p>
                <p className="text-xs text-gray-400">Reprogrammés</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-500/10 border-gray-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-gray-400" />
              <div>
                <p className="text-2xl font-bold text-white">
                  {appointments.filter(a => a.status === 'completed').length}
                </p>
                <p className="text-xs text-gray-400">Terminés</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, visa, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white/5 border-white/10 text-white" data-testid="status-filter">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10">
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Appointments List */}
      <Card className="bg-[#0F172A] border-white/10">
        <CardContent className="p-0">
          {filteredAppointments.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun rendez-vous trouvé</p>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {filteredAppointments.map((apt) => {
                const StatusIcon = statusConfig[apt.status]?.icon || Clock;
                const isOnline = apt.meeting_type === 'online';
                return (
                  <div 
                    key={apt.id}
                    className="p-4 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => { 
                      setSelectedAppointment(apt); 
                      setAgentNotes(apt.agent_notes || '');
                      setShowDetail(true); 
                    }}
                    data-testid={`apt-row-${apt.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isOnline ? 'bg-purple-500/20' : 'bg-sky-500/20'
                        }`}>
                          {isOnline ? (
                            <Video className="w-6 h-6 text-purple-400" />
                          ) : (
                            <MapPin className="w-6 h-6 text-sky-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white">{apt.client_name}</p>
                          <p className="text-sm text-gray-400">
                            {apt.visa_type} - {apt.destination}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right hidden md:block">
                          <p className="text-sm text-white">
                            {apt.confirmed_date || apt.preferred_date}
                          </p>
                          <p className="text-xs text-gray-400">
                            {apt.confirmed_time || apt.preferred_time}
                          </p>
                        </div>
                        <Badge className={statusConfig[apt.status]?.color}>
                          {statusConfig[apt.status]?.label}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-gray-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                selectedAppointment?.meeting_type === 'online' ? 'bg-purple-500/20' : 'bg-sky-500/20'
              }`}>
                {selectedAppointment?.meeting_type === 'online' ? (
                  <Video className="w-5 h-5 text-purple-400" />
                ) : (
                  <MapPin className="w-5 h-5 text-sky-400" />
                )}
              </div>
              Rendez-vous - {selectedAppointment?.reference}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedAppointment?.visa_type} ({selectedAppointment?.destination})
            </DialogDescription>
          </DialogHeader>

          {selectedAppointment && (
            <div className="space-y-4 py-4">
              {/* Status */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                <div>
                  <p className="text-xs text-gray-400">Statut</p>
                  <Badge className={`mt-1 ${statusConfig[selectedAppointment.status]?.color}`}>
                    {statusConfig[selectedAppointment.status]?.label}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Date/Heure</p>
                  <p className="text-sm font-medium text-white">
                    {selectedAppointment.confirmed_date || selectedAppointment.preferred_date} à {selectedAppointment.confirmed_time || selectedAppointment.preferred_time}
                  </p>
                </div>
              </div>

              {/* Client Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gray-400" />
                  <span className="text-white">{selectedAppointment.client_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="text-white">{selectedAppointment.client_email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-white">{selectedAppointment.client_phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  {selectedAppointment.meeting_type === 'online' ? (
                    <>
                      <Video className="w-4 h-4 text-purple-400" />
                      <span className="text-purple-400">Rendez-vous en ligne</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 text-sky-400" />
                      <span className="text-sky-400">En présentiel (Bureau Kinshasa)</span>
                    </>
                  )}
                </div>
              </div>

              {/* Client Notes */}
              {selectedAppointment.notes && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-xs text-amber-400 mb-1">Note du client</p>
                  <p className="text-sm text-white">{selectedAppointment.notes}</p>
                </div>
              )}

              {/* Agent Notes */}
              <div>
                <Label className="text-gray-400 text-xs">Notes agent</Label>
                <Textarea
                  placeholder="Ajouter une note..."
                  value={agentNotes}
                  onChange={(e) => setAgentNotes(e.target.value)}
                  className="mt-1 bg-white/5 border-white/10 text-white text-sm"
                  rows={2}
                />
              </div>

              {/* Actions */}
              {selectedAppointment.status === 'pending' && (
                <div className="space-y-2 pt-2">
                  <Button 
                    onClick={confirmAppointment}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="confirm-apt"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Confirmer le RDV
                  </Button>
                  <Button 
                    onClick={() => setShowReschedule(true)}
                    variant="outline"
                    className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    data-testid="reschedule-apt"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Proposer une autre date
                  </Button>
                  <Button 
                    onClick={cancelAppointment}
                    variant="outline"
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="cancel-apt"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Annuler
                  </Button>
                </div>
              )}

              {(selectedAppointment.status === 'confirmed' || selectedAppointment.status === 'rescheduled') && (
                <div className="space-y-2 pt-2">
                  <Button 
                    onClick={markCompleted}
                    className="w-full bg-sky-600 hover:bg-sky-700"
                    data-testid="complete-apt"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Marquer comme terminé
                  </Button>
                  <Button 
                    onClick={() => setShowReschedule(true)}
                    variant="outline"
                    className="w-full border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" /> Reprogrammer
                  </Button>
                  <Button 
                    onClick={cancelAppointment}
                    variant="outline"
                    className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Annuler
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={showReschedule} onOpenChange={setShowReschedule}>
        <DialogContent className="max-w-sm bg-[#0F172A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Reprogrammer le rendez-vous</DialogTitle>
            <DialogDescription className="text-gray-400">
              Sélectionnez une nouvelle date et un créneau
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-400 text-xs">Nouvelle date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                min={getMinDate()}
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="new-date"
              />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Nouveau créneau</Label>
              <Select value={newTime} onValueChange={setNewTime}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white" data-testid="new-time">
                  <SelectValue placeholder="Choisir un créneau" />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10">
                  {availableSlots.map((slot) => (
                    <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={() => setShowReschedule(false)}
                variant="outline"
                className="flex-1 border-white/10"
              >
                Annuler
              </Button>
              <Button 
                onClick={rescheduleAppointment}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="confirm-reschedule"
              >
                Confirmer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppointmentsPage;
